import { useState, type ReactNode } from "react";
import { IconChevronDown, IconRefresh } from "@tabler/icons-react";
import {
  useOutlookSubscriptionDebugQuery,
  useUserInfoQuery,
} from "../../../api/queries/userQueries";
import { useOutgoingMailboxQuery } from "../../../api/queries/organizationQueries";
import { useAuth } from "../../../hooks/useAuth";
import { displayLastUpdated } from "../../../utils/display";

function formatTimestamp(value: string | null | undefined): string {
  if (!value?.trim()) {
    return "Never";
  }
  return displayLastUpdated(value);
}

function StatusBadge({
  success,
  label,
}: {
  success: boolean | null | undefined;
  label: string;
}) {
  if (success === true) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
        {label}: OK
      </span>
    );
  }
  if (success === false) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200">
        {label}: Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
      {label}: Unknown
    </span>
  );
}

function DebugRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <dt className="shrink-0 text-xs font-medium text-slate-500 sm:w-40">
        {label}
      </dt>
      <dd className="min-w-0 text-sm text-slate-800">{children}</dd>
    </div>
  );
}

export function OutlookSubscriptionDebugPanel() {
  const { roles } = useAuth();
  const isAdmin = (roles ?? []).some((role) => role === "ADMIN");
  const { data: userInfo } = useUserInfoQuery();
  const { data: outgoingMailbox } = useOutgoingMailboxQuery({
    enabled: isAdmin,
  });
  const [expanded, setExpanded] = useState(false);

  const isMicrosoftMailbox =
    userInfo?.oauth_provider === "microsoft" ||
    Boolean(
      outgoingMailbox?.uses_official_email &&
        outgoingMailbox.mailbox_connected &&
        !outgoingMailbox.acting_user_is_mailbox_owner,
    );

  const queryEnabled = isAdmin && isMicrosoftMailbox && expanded;
  const { data, isLoading, isFetching, refetch, error } =
    useOutlookSubscriptionDebugQuery(queryEnabled);

  if (!isAdmin || !isMicrosoftMailbox) {
    return null;
  }

  return (
    <div className="shrink-0 border-b border-amber-200 bg-amber-50/60">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
        aria-expanded={expanded}
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-amber-900">
          Outlook subscription debug
        </span>
        <span className="inline-flex items-center gap-1 text-xs text-amber-800">
          Admin
          <IconChevronDown
            size={14}
            className={`transition-transform ${expanded ? "rotate-180" : ""}`}
            aria-hidden
          />
        </span>
      </button>

      {expanded ? (
        <div className="space-y-3 border-t border-amber-200 px-3 py-3">
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50"
            >
              <IconRefresh
                size={14}
                className={isFetching ? "animate-spin" : undefined}
                aria-hidden
              />
              Refresh
            </button>
          </div>

          {isLoading && !data ? (
            <p className="text-sm text-amber-900/80">Loading debug info…</p>
          ) : error ? (
            <p className="text-sm text-red-700">
              Could not load subscription debug info.
            </p>
          ) : data && !data.applicable ? (
            <p className="text-sm text-amber-900/80">
              {data.reason === "not_outlook"
                ? "Outlook watch is not used for this mailbox."
                : "Subscription debug is not applicable for this mailbox."}
            </p>
          ) : data ? (
            <dl className="space-y-3">
              {!data.acting_user_is_mailbox_owner ? (
                <p className="rounded-md border border-amber-200 bg-white/80 px-2.5 py-2 text-xs text-amber-900">
                  Watch runs on the mailbox owner account.
                </p>
              ) : null}

              <div className="rounded-md border border-amber-200 bg-white/80 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Watch setup
                </p>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge
                      success={data.last_setup_success}
                      label="Setup"
                    />
                    {data.is_expired ? (
                      <span className="inline-flex items-center rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700 ring-1 ring-orange-200">
                        Expired
                      </span>
                    ) : null}
                    {data.watch_configured ? (
                      <span className="text-xs text-slate-500">
                        Subscription active
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500">
                        No subscription configured
                      </span>
                    )}
                  </div>
                  <DebugRow label="Last setup">
                    {formatTimestamp(data.last_setup_at)}
                  </DebugRow>
                  {data.expiration_utc ? (
                    <DebugRow label="Expires">
                      {formatTimestamp(data.expiration_utc)}
                    </DebugRow>
                  ) : null}
                  {data.last_setup_error ? (
                    <p className="text-xs text-red-700">{data.last_setup_error}</p>
                  ) : null}
                </div>
              </div>

              <div className="rounded-md border border-amber-200 bg-white/80 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Last webhook
                </p>
                <DebugRow label="Notification received">
                  {formatTimestamp(data.last_notification_at)}
                </DebugRow>
                {data.last_notification_enqueued_at ? (
                  <DebugRow label="Enqueued">
                    {formatTimestamp(data.last_notification_enqueued_at)}
                  </DebugRow>
                ) : null}
                {data.last_notification_enqueue_error ? (
                  <p className="mt-1 text-xs text-red-700">
                    {data.last_notification_enqueue_error}
                  </p>
                ) : null}
              </div>

              <div className="rounded-md border border-amber-200 bg-white/80 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Last processed push
                </p>
                <div className="space-y-2">
                  <StatusBadge
                    success={data.last_push_processing_success}
                    label="Processing"
                  />
                  <DebugRow label="Processed at">
                    {formatTimestamp(data.last_push_processed_at)}
                  </DebugRow>
                  {data.last_push_processing_error ? (
                    <p className="text-xs text-red-700">
                      {data.last_push_processing_error}
                    </p>
                  ) : null}
                </div>
              </div>
            </dl>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
